import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const Privacy = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">Legal</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(32px, 5vw, 60px)', marginBottom: '16px' }}>Privacy Policy</h1>
            <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', lineHeight: 1.7, marginBottom: '8px' }}>
              Your privacy matters to us. Here's how we protect and use your information.
            </p>
            <p style={{ fontSize: '13px', color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)' }}>Last updated: January 1, 2025</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bw-section-surface">
        <div className="max-w-4xl mx-auto space-y-10">

          {/* Intro */}
          <div className="bw-card">
            <div className="bw-card-body">
              <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.8, fontSize: '16px' }}>
                Bright Wings ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this privacy policy carefully.
              </p>
            </div>
          </div>

          {/* Each policy section */}
          {[
            {
              title: 'Information We Collect',
              content: (
                <div>
                  <h3 style={{ ...hStyle, fontSize: '18px', marginBottom: '12px' }}>Personal Information</h3>
                  <p style={{ color: 'var(--bw-text-mid)', marginBottom: '12px', lineHeight: 1.7 }}>We collect information that you provide directly to us, including:</p>
                  <ul className="space-y-2 mb-6">
                    {['Name and contact information (email address, phone number)', 'Company name and professional details', 'Project details and messages you send through our contact forms', 'Account credentials and authentication information'].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span style={{ color: 'var(--bw-blue)', flexShrink: 0, marginTop: '2px' }}>•</span>
                        <span style={{ color: 'var(--bw-text-mid)', fontSize: '15px' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <h3 style={{ ...hStyle, fontSize: '18px', marginBottom: '12px' }}>Automatically Collected Information</h3>
                  <p style={{ color: 'var(--bw-text-mid)', marginBottom: '12px', lineHeight: 1.7 }}>When you visit our website, we automatically collect certain information, including:</p>
                  <ul className="space-y-2">
                    {['Browser type and version', 'IP address and geographic location', 'Pages visited and time spent on pages', 'Referring website or search engine'].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span style={{ color: 'var(--bw-blue)', flexShrink: 0, marginTop: '2px' }}>•</span>
                        <span style={{ color: 'var(--bw-text-mid)', fontSize: '15px' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            },
            {
              title: 'How We Use Your Information',
              content: (
                <div>
                  <p style={{ color: 'var(--bw-text-mid)', marginBottom: '12px', lineHeight: 1.7 }}>We use the information we collect to:</p>
                  <ul className="space-y-2">
                    {['Respond to your inquiries and provide customer support', 'Deliver our services and manage your projects', 'Send you updates, newsletters, and marketing communications (with your consent)', 'Improve our website and services', 'Detect, prevent, and address technical issues or fraud', 'Comply with legal obligations and enforce our terms'].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span style={{ color: 'var(--bw-blue)', flexShrink: 0, marginTop: '2px' }}>•</span>
                        <span style={{ color: 'var(--bw-text-mid)', fontSize: '15px' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            },
            {
              title: 'Data Storage & Security',
              content: (
                <div>
                  <p style={{ color: 'var(--bw-text-mid)', marginBottom: '12px', lineHeight: 1.7 }}>We implement industry-standard security measures to protect your data, including:</p>
                  <ul className="space-y-2 mb-4">
                    {['Encryption of data in transit and at rest', 'Secure cloud infrastructure with regular backups', 'Access controls and authentication mechanisms', 'Regular security audits and monitoring'].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span style={{ color: 'var(--bw-blue)', flexShrink: 0, marginTop: '2px' }}>•</span>
                        <span style={{ color: 'var(--bw-text-mid)', fontSize: '15px' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>
                    However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.
                  </p>
                </div>
              ),
            },
            {
              title: 'Third-Party Services',
              content: (
                <div>
                  <p style={{ color: 'var(--bw-text-mid)', marginBottom: '16px', lineHeight: 1.7 }}>We use trusted third-party services to operate our business:</p>
                  {[
                    { name: 'Auth0', desc: 'We use Auth0 for authentication and user management. Auth0\'s privacy policy is available at ', link: { href: 'https://auth0.com/privacy', label: 'auth0.com/privacy' } },
                    { name: 'Amazon Web Services (AWS)', desc: 'We use AWS for cloud infrastructure, data storage, and email services. AWS\'s privacy policy is available at ', link: { href: 'https://aws.amazon.com/privacy/', label: 'aws.amazon.com/privacy' } },
                  ].map((service, i) => (
                    <div key={i} className="mb-4">
                      <h3 style={{ ...hStyle, fontSize: '16px', marginBottom: '6px' }}>{service.name}</h3>
                      <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>
                        {service.desc}
                        <a href={service.link.href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--bw-blue)', textDecoration: 'underline' }}>{service.link.label}</a>.
                      </p>
                    </div>
                  ))}
                  <div>
                    <h3 style={{ ...hStyle, fontSize: '16px', marginBottom: '6px' }}>Analytics</h3>
                    <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>We may use analytics services to understand how visitors use our website. These services may collect information about your device, browsing actions, and usage patterns.</p>
                  </div>
                </div>
              ),
            },
            {
              title: 'Cookies & Tracking Technologies',
              content: (
                <div>
                  <p style={{ color: 'var(--bw-text-mid)', marginBottom: '12px', lineHeight: 1.7 }}>We use cookies and similar tracking technologies. We use:</p>
                  <ul className="space-y-2 mb-4">
                    {[
                      { label: 'Essential cookies:', desc: 'Required for the website to function properly' },
                      { label: 'Preference cookies:', desc: 'Remember your settings' },
                      { label: 'Analytics cookies:', desc: 'Help us understand how visitors use our site' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span style={{ color: 'var(--bw-blue)', flexShrink: 0, marginTop: '2px' }}>•</span>
                        <span style={{ color: 'var(--bw-text-mid)', fontSize: '15px' }}><strong style={{ color: 'var(--bw-dark)' }}>{item.label}</strong> {item.desc}</span>
                      </li>
                    ))}
                  </ul>
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>You can control cookies through your browser settings.</p>
                </div>
              ),
            },
            {
              title: 'Your Privacy Rights',
              content: (
                <div>
                  <p style={{ color: 'var(--bw-text-mid)', marginBottom: '12px', lineHeight: 1.7 }}>Depending on your location, you may have the following rights:</p>
                  <ul className="space-y-2 mb-4">
                    {[
                      { label: 'Access:', desc: 'Request a copy of the personal information we hold about you' },
                      { label: 'Correction:', desc: 'Request correction of inaccurate or incomplete information' },
                      { label: 'Deletion:', desc: 'Request deletion of your personal information' },
                      { label: 'Portability:', desc: 'Request a copy of your data in a machine-readable format' },
                      { label: 'Opt-out:', desc: 'Unsubscribe from marketing communications at any time' },
                      { label: 'Object:', desc: 'Object to processing of your personal information' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span style={{ color: 'var(--bw-blue)', flexShrink: 0, marginTop: '2px' }}>•</span>
                        <span style={{ color: 'var(--bw-text-mid)', fontSize: '15px' }}><strong style={{ color: 'var(--bw-dark)' }}>{item.label}</strong> {item.desc}</span>
                      </li>
                    ))}
                  </ul>
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>
                    To exercise any of these rights, please contact us at{' '}
                    <a href="mailto:privacy@brightwings.io" style={{ color: 'var(--bw-blue)', textDecoration: 'underline' }}>privacy@brightwings.io</a>.
                  </p>
                </div>
              ),
            },
            {
              title: 'Data Retention',
              content: <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.8, fontSize: '15px' }}>We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.</p>,
            },
            {
              title: "Children's Privacy",
              content: <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.8, fontSize: '15px' }}>Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us, and we will take steps to delete such information.</p>,
            },
            {
              title: 'Changes to This Privacy Policy',
              content: <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.8, fontSize: '15px' }}>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.</p>,
            },
            {
              title: 'Contact Us',
              content: (
                <div className="space-y-2">
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px', marginBottom: '12px' }}>If you have any questions about this Privacy Policy, please contact us:</p>
                  <p style={{ color: 'var(--bw-text-mid)', fontSize: '15px' }}><strong style={{ color: 'var(--bw-dark)' }}>Email:</strong>{' '}<a href="mailto:privacy@brightwings.io" style={{ color: 'var(--bw-blue)', textDecoration: 'underline' }}>privacy@brightwings.io</a></p>
                  <p style={{ color: 'var(--bw-text-mid)', fontSize: '15px' }}><strong style={{ color: 'var(--bw-dark)' }}>General Inquiries:</strong>{' '}<a href="mailto:hello@brightwings.io" style={{ color: 'var(--bw-blue)', textDecoration: 'underline' }}>hello@brightwings.io</a></p>
                </div>
              ),
            },
          ].map((section, i) => (
            <div key={i}>
              <h2 style={{ ...hStyle, fontSize: 'clamp(22px, 2.5vw, 28px)', marginBottom: '20px' }}>{section.title}</h2>
              <div className="bw-card">
                <div className="bw-card-body">{section.content}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Privacy;
